import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus, Trash2, Pencil, TrendingUp, TrendingDown, Wallet, Boxes } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { fmtIDR, fmtDate, todayStr } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Finance() {
  const { t } = useLang();
  const { outlet } = useOutletContext() || {};
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [cashflows, setCashflows] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [kind, setKind] = useState("expense");
  const [tab, setTab] = useState("cashflow");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: "", description: "", amount: 0, date: todayStr() });

  const load = useCallback(async () => {
    try {
      const [s, e, c, inc] = await Promise.all([
        api.get("/finance/summary"), api.get("/finance/expenses"), api.get("/finance/cashflow"), api.get("/finance/incomes"),
      ]);
      setSummary(s.data);
      setExpenses(e.data);
      setCashflows(c.data);
      setIncomes(inc.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const endpoint = kind === "income" ? "incomes" : "expenses";

  const save = async () => {
    try {
      const payload = { ...form, outlet_id: outlet || "" };
      if (editing) await api.put(`/finance/${endpoint}/${editing.id}`, payload);
      else await api.post(`/finance/${endpoint}`, payload);
      toast.success("OK");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id, k) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/finance/${k === "income" ? "incomes" : "expenses"}/${id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const cards = summary ? [
    { key: "cashIn", value: summary.cash_in, icon: TrendingUp, color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40" },
    { key: "cashOut", value: summary.cash_out, icon: TrendingDown, color: "text-rose-600 bg-rose-50 dark:bg-rose-950/40" },
    { key: "cashBalance", value: summary.cash_balance, icon: Wallet, color: "text-amber-600 bg-amber-50 dark:bg-amber-950/40" },
    { key: "stockValue", value: summary.stock_value, icon: Boxes, color: "text-cyan-600 bg-cyan-50 dark:bg-cyan-950/40" },
  ] : [];

  return (
    <div data-testid="finance-page" className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight flex-1">{t("nav_finance")}</h1>
        <Button data-testid="expense-add-btn" onClick={() => { setKind(tab === "income" ? "income" : "expense"); setEditing(null); setForm({ category: "", description: "", amount: 0, date: todayStr() }); setOpen(true); }} className="bg-amber-600 hover:bg-amber-700 text-white">
          <Plus className="w-4 h-4 mr-1.5" /> {tab === "income" ? t("income") : t("expenses")}
        </Button>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.key} data-testid={`finance-${c.key}`} className="bg-card border border-border rounded-xl p-4 shadow-xs">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${c.color}`}><c.icon className="w-4 h-4" /></div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{t(c.key)}</div>
            <div className="text-xl font-bold font-mono tracking-tight">{fmtIDR(c.value)}</div>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="finance-tabs">
          <TabsTrigger value="cashflow" data-testid="tab-cashflow">{t("cashflow")}</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">{t("expenses")}</TabsTrigger>
          <TabsTrigger value="income" data-testid="tab-income">{t("income")}</TabsTrigger>
        </TabsList>
        <TabsContent value="cashflow">
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs mt-3">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-muted/50">
                  {[t("date"), "Source", "Note", t("cashIn"), t("cashOut")].map((h) => <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>)}
                </tr></thead>
                <tbody>
                  {cashflows.map((c) => (
                    <tr key={c.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{fmtDate(c.created_at)}</td>
                      <td className="px-4 py-2.5"><span className="bg-muted px-2.5 py-0.5 rounded-full text-xs font-semibold">{c.source}</span></td>
                      <td className="px-4 py-2.5 text-muted-foreground">{c.note || "-"}</td>
                      <td className="px-4 py-2.5 font-mono text-emerald-600">{c.type === "in" ? fmtIDR(c.amount) : ""}</td>
                      <td className="px-4 py-2.5 font-mono text-rose-600">{c.type === "out" ? fmtIDR(c.amount) : ""}</td>
                    </tr>
                  ))}
                  {cashflows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">-</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="expenses">
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs mt-3">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-muted/50">
                  {[t("date"), t("category"), "Deskripsi", "Amount", t("actions")].map((h) => <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>)}
                </tr></thead>
                <tbody>
                  {expenses.map((e) => (
                    <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{e.date}</td>
                      <td className="px-4 py-2.5 font-medium">{e.category}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{e.description}</td>
                      <td className="px-4 py-2.5 font-mono text-rose-600">{fmtIDR(e.amount)}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <button data-testid={`expense-edit-${e.id}`} onClick={() => { setKind("expense"); setEditing(e); setForm({ category: e.category, description: e.description, amount: e.amount, date: e.date }); setOpen(true); }} className="p-1.5 text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`expense-delete-${e.id}`} onClick={() => remove(e.id, "expense")} className="p-1.5 text-muted-foreground hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                  {expenses.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">-</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="income">
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs mt-3">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-muted/50">
                  {[t("date"), t("category"), "Deskripsi", "Amount", t("actions")].map((h) => <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>)}
                </tr></thead>
                <tbody>
                  {incomes.map((e) => (
                    <tr key={e.id} data-testid={`income-row-${e.id}`} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{e.date}</td>
                      <td className="px-4 py-2.5 font-medium">{e.category}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{e.description}</td>
                      <td className="px-4 py-2.5 font-mono text-emerald-600">{fmtIDR(e.amount)}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <button data-testid={`income-edit-${e.id}`} onClick={() => { setKind("income"); setEditing(e); setForm({ category: e.category, description: e.description, amount: e.amount, date: e.date }); setOpen(true); }} className="p-1.5 text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`income-delete-${e.id}`} onClick={() => remove(e.id, "income")} className="p-1.5 text-muted-foreground hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                  {incomes.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">-</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="expense-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? t("edit") : t("add")} {kind === "income" ? t("income") : t("expenses")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("category")}</Label>
              <Input data-testid="expense-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Gaji / Sewa / Utilitas..." className="mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Deskripsi</Label>
              <Input data-testid="expense-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Amount</Label>
                <Input data-testid="expense-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} className="mt-1.5" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("date")}</Label>
                <Input data-testid="expense-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
              <Button data-testid="expense-save-btn" onClick={save} className="bg-amber-600 hover:bg-amber-700 text-white">{t("save")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
